import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import { useForm } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, Clock, DollarSign, Zap, Save, Lock, ArrowRight, AlertCircle, Route, Shield, Loader2, Users } from 'lucide-react';
import { OperatorPanel } from '../components/OperatorPanel';
import { ClientsPanel } from '../components/ClientsPanel';
import { signInAnonymously } from 'firebase/auth';
import { auth } from '../firebase';

interface AdminFormData {
  baseFare: number;
  pricePerKm: number;
  extraStopFee: number;
  returnFee: number;
  longDistanceThresholdKm: number;
  longDistanceSurchargePercent: number;
  surgeMultiplier: number;
  surgeStartHour: string;
  surgeEndHour: string;
}

export function Admin() {
  const { config, updateConfig, isSurgeActive, configLoaded } = useAdmin();
  const { register, handleSubmit, reset } = useForm<AdminFormData>({ defaultValues: config });
  const navigate = useNavigate();

  // Assim que os dados chegarem do Realtime Database, preenche o formulário
  useEffect(() => {
    if (configLoaded) {
      reset(config);
    }
  }, [configLoaded, config, reset]);

  const [activeTab, setActiveTab] = useState<'operador' | 'precos' | 'clientes'>('operador');

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('adminAuth') === 'true';
  });
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const parseDecimal = (val: any) => {
    if (!val) return 0;
    return parseFloat(String(val).replace(',', '.'));
  };

  const sanitizeTime = (val: string) => {
    return val ? val.trim().substring(0, 5) : '00:00';
  };

  const onSubmit = (data: AdminFormData) => {
    updateConfig({
      baseFare: parseDecimal(data.baseFare),
      pricePerKm: parseDecimal(data.pricePerKm),
      extraStopFee: parseDecimal(data.extraStopFee),
      returnFee: parseDecimal(data.returnFee),
      longDistanceThresholdKm: parseDecimal(data.longDistanceThresholdKm),
      longDistanceSurchargePercent: parseDecimal(data.longDistanceSurchargePercent),
      surgeMultiplier: parseDecimal(data.surgeMultiplier),
      surgeStartHour: sanitizeTime(data.surgeStartHour),
      surgeEndHour: sanitizeTime(data.surgeEndHour),
    });
    
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'Abh1004@') {
      setLoggingIn(true);
      try {
        // Faz login anônimo para garantir request.auth válido no Firestore
        await signInAnonymously(auth);
      } catch (_e) {
        // Se o login anônimo falhar (ex: desabilitado no console),
        // prosseguimos mesmo assim, pois a senha já é a barreira principal
        console.warn('signInAnonymously falhou — operando sem auth do Firestore.');
      }
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuth', 'true');
      setError(false);
      setLoggingIn(false);
    } else {
      setError(true);
    }
  };

  if (!isAuthenticated) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }} 
        animate={{ opacity: 1, scale: 1 }} 
        className="max-w-md mx-auto mt-10"
      >
        <div className="glassmorphism rounded-3xl p-8 text-center relative overflow-hidden">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-800 to-brand-navy mx-auto flex items-center justify-center text-white mb-6 shadow-lg">
            <Lock size={28} />
          </div>
          <h1 className="text-2xl font-extrabold text-brand-navy mb-2">Acesso Restrito</h1>
          <p className="text-sm text-slate-500 mb-8">Digite a senha para acessar o painel de operações.</p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <input 
                type="password" 
                value={password}
                onChange={e => { setPassword(e.target.value); setError(false); }}
                placeholder="Senha de acesso" 
                className="w-full rounded-xl border border-slate-200/80 bg-white/70 p-4 text-center text-lg text-slate-800 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15 outline-none transition-all duration-200"
                autoFocus
              />
            </div>
            
            <AnimatePresence>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }} 
                  animate={{ opacity: 1, height: 'auto' }} 
                  exit={{ opacity: 0, height: 0 }}
                  className="text-red-500 text-sm font-semibold flex items-center justify-center gap-1.5"
                >
                  <AlertCircle size={14} /> Senha incorreta
                </motion.div>
              )}
            </AnimatePresence>

            <button 
              type="submit" 
              disabled={loggingIn}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-brand-navy to-slate-800 text-white font-bold py-3.5 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl hover:shadow-slate-800/20 transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loggingIn ? (
                <><Loader2 size={18} className="animate-spin" /> Autenticando...</>
              ) : (
                <>Entrar <ArrowRight size={18} /></>
              )}
            </button>
          </form>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      className="max-w-6xl mx-auto space-y-6"
    >
      {/* Navegação de Abas do Admin */}
      <div className="flex justify-between items-center bg-white p-2.5 rounded-2xl border border-slate-100 shadow-sm">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('operador')}
            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 ${
              activeTab === 'operador'
                ? 'bg-slate-900 text-white shadow-md'
                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <Shield size={16} className="text-brand-green" /> Central do Operador
          </button>
          <button
            onClick={() => setActiveTab('precos')}
            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 ${
              activeTab === 'precos'
                ? 'bg-slate-900 text-white shadow-md'
                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <Settings size={16} /> Tabela de Preços (quoteConfig)
          </button>
          <button
            onClick={() => setActiveTab('clientes')}
            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 ${
              activeTab === 'clientes'
                ? 'bg-slate-900 text-white shadow-md'
                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <Users size={16} /> Clientes
          </button>
        </div>
      </div>

      {activeTab === 'operador' ? (
        <OperatorPanel />
      ) : activeTab === 'clientes' ? (
        <ClientsPanel />
      ) : (
        <div className="max-w-2xl mx-auto glassmorphism rounded-3xl p-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-blue to-brand-electric flex items-center justify-center text-white">
              <Settings size={20} />
            </div>
            <h1 className="text-2xl font-bold text-brand-navy">Variáveis de Preço</h1>
          </div>
          <p className="text-slate-500 mb-8 ml-[52px]">Defina as variáveis de preço do motor de cotação.</p>

          {isSurgeActive && (
            <div className="mb-6 flex items-center gap-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-4 text-sm font-medium">
              <Zap size={18} className="text-amber-500 shrink-0" />
              Surge Pricing está <strong className="ml-1">ATIVO</strong> agora ({config.surgeMultiplier}x multiplicador)
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                <DollarSign size={14} /> Tarifas Base
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Tarifa Mínima (R$)</label>
                  <input {...register('baseFare')} type="number" step="0.01" className="w-full rounded-xl border border-slate-200 shadow-sm p-3 bg-white/80 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Valor do KM (R$)</label>
                  <input {...register('pricePerKm')} type="number" step="0.01" className="w-full rounded-xl border border-slate-200 shadow-sm p-3 bg-white/80 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Adicional por Parada (R$)</label>
                  <input {...register('extraStopFee')} type="number" step="0.01" className="w-full rounded-xl border border-slate-200 shadow-sm p-3 bg-white/80 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Taxa de Retorno (R$)</label>
                  <input {...register('returnFee')} type="number" step="0.01" className="w-full rounded-xl border border-slate-200 shadow-sm p-3 bg-white/80 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all" />
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-6">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                <Route size={14} /> Taxa de Retorno Automática (Longa Distância)
              </h2>
              <p className="text-xs text-slate-400 mb-4">
                Quando a distância de ida ultrapassar o limite, aplica automaticamente um acréscimo sobre o valor total (motoboy precisa retornar).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Distância Limite (km)</label>
                  <input {...register('longDistanceThresholdKm')} type="number" step="1" className="w-full rounded-xl border border-slate-200 shadow-sm p-3 bg-white/80 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Acréscimo (%)</label>
                  <input {...register('longDistanceSurchargePercent')} type="number" step="1" className="w-full rounded-xl border border-slate-200 shadow-sm p-3 bg-white/80 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all" />
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-6">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Clock size={14} /> Dinâmica de Preços (Surge Pricing)
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Multiplicador</label>
                  <input {...register('surgeMultiplier')} type="number" step="0.1" className="w-full rounded-xl border border-slate-200 shadow-sm p-3 bg-white/80 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Hora Início</label>
                  <input {...register('surgeStartHour')} type="time" className="w-full rounded-xl border border-slate-200 shadow-sm p-3 bg-white/80 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Hora Fim</label>
                  <input {...register('surgeEndHour')} type="time" className="w-full rounded-xl border border-slate-200 shadow-sm p-3 bg-white/80 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all" />
                </div>
              </div>
            </div>

            <div className="pt-4">
              <button type="submit" className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-brand-blue to-brand-electric text-white font-semibold py-3.5 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl hover:shadow-brand-blue/25 transform hover:-translate-y-0.5 active:translate-y-0">
                <Save size={18} />
                Salvar Configurações
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Toast Feedback */}
      <AnimatePresence>
        {showToast && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 right-6 z-50 bg-emerald-500 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 font-bold"
          >
            <Save size={20} />
            Tabela de preços atualizada!
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

